/**
 *  @swagger
 *  definitions:
 *    RatingParams:
 *      type: object
 *      properties:
 *        RegionId:
 *          description: ID of the associated Region
 *          type: string
 *        ReferenceId:
 *          description: ID of the associated Rating
 *          type: string
 *        Uid:
 *          description: Uid of the Rating
 *          type: string
 *        RegistrationNo:
 *          description: Registration No of the Rating
 *          type: string
 *        Type:
 *          description: Type of the Rating
 *          $ref: '#/components/enums/RatingType'
 *        Values:
 *          description: Values of the Rating
 *          type: string
 *        File:
 *          description: File of the Rating
 *          type: string
 *        Latest:
 *          description: Latest of the Rating
 *          type: boolean
 *
 *  @swagger
 *  definitions:
 *    RatingCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RatingParams'
 *        - type: object
 *          required:
 *            - RegionId
 *            - RegistrationNo
 *            - Type
 *            - Values
 *            - Latest
 *
 *  @swagger
 *  definitions:
 *    RatingQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the Rating
 *              type: string
 *        - $ref: '#/definitions/RatingParams'
 *
 *  @swagger
 *  definitions:
 *    RatingUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RatingQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    Rating:
 *      allOf:
 *        - $ref: '#/definitions/RatingCreateParams'
 *        - $ref: '#/definitions/RatingUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      Rating:
 *        allOf:
 *          - $ref: '#/definitions/Rating'
 *          - type: object
 *            properties:
 *              Region:
 *                description: Region of the Rating
 *                $ref: '#/components/schemas/Region'
 *              Reference:
 *                description: Reference of the Rating
 *                $ref: '#/components/schemas/Rating'
 *          - type: object
 *            properties:
 *              KeyValueName:
 *                description: Key Value Name of the Rating
 *                type: string
 *              KeyValue:
 *                description: Key Value of the Rating
 *                type: integer
 */
// Helpers
import _ from 'lodash';

// Enums
import {
  ratingKeyValue,
  RatingType,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

type $VirtualParams = {
  KeyValue?: number;
  KeyValueName: string;
};

export type $RelationIds = {
  ReferenceId: null | string;
  RegionId: string;
};

export type $Params = {
  File: null | string;
  Latest: boolean;
  RegistrationNo: string;
  Type: RatingType;
  Uid: string;
  Values: Record<string, unknown>;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Entries?: Array<$Model<'Entry'>>;
  Reference?: $Model<'Rating'>;
  RegattaEntries?: Array<$Model<'RegattaEntry'>>;
  Region?: $Model<'Region'>;
};

export type $ModelParams = $Associations & $CreateParams & $VirtualParams & {
  id: string;
};

type $Fields = $Params & $VirtualParams & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  File: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  KeyValue: {
    get(): number | undefined {
      const type: RatingType = this.get('Type');

      const values: Record<string, number> = this.get('Values');

      return _.get(
        values,
        ratingKeyValue[type],
      );
    },

    type: new Sequelize.VIRTUAL(
      Sequelize.STRING,
      ['Type', 'Values'],
    ),
  },
  KeyValueName: {
    get(): string {
      const type: RatingType = this.get('Type');

      return ratingKeyValue[type];
    },

    type: new Sequelize.VIRTUAL(
      Sequelize.STRING,
      ['Type', 'Values'],
    ),
  },
  Latest: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  RegistrationNo: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Type: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(RatingType)),
    validate: {
      notEmpty: true,
    },
  },
  Uid: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Values: {
    allowNull: false,
    type: Sequelize.JSON,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'Rating'> => {
  const Rating = stack.define(
    'Rating',
    fields(Sequelize),
    {
      tableName: 'Ratings',
    },
  );

  Rating.associate = (models: $ModelClasses) => {
    models.Rating.belongsTo(
      models.Region,
      {
        as: 'Region',
        foreignKey: 'RegionId',
      },
    );
    models.Rating.belongsTo(
      models.Rating,
      {
        as: 'Reference',
        constraints: false,
        foreignKey: 'ReferenceId',
      },
    );
    models.Rating.belongsToMany(
      models.Entry,
      {
        as: 'Entries',
        foreignKey: 'RatingId',
        otherKey: 'EntryId',
        through: models.EntryRating,
      },
    );
    models.Rating.belongsToMany(
      models.RegattaEntry,
      {
        as: 'RegattaEntries',
        foreignKey: 'RatingId',
        otherKey: 'RegattaEntryId',
        through: models.RegattaEntryRating,
      },
    );
  };

  Rating.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return Rating;
});
